import React, { useState, useEffect } from 'react';
import { Menu, User, Moon, Sun, Settings, LogOut, Calculator, ListTodo, Calendar, Wrench } from 'lucide-react';
import { useAppStore } from '../../store';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface HeaderProps {
  title: string;
  toggleMobileSidebar: () => void;
}

const Header: React.FC<HeaderProps> = ({ title, toggleMobileSidebar }) => {
  const { darkMode, toggleDarkMode } = useAppStore();
  const { user, userRole, signOut, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState<string>('');
  
  // Get user initials from email
  const getUserInitials = () => {
    if (!user?.email) return 'U';
    return user.email.substring(0, 2).toUpperCase();
  };
  
  // Get role display name
  const getRoleDisplay = () => {
    return userRole || 'Usuario';
  };

  // Update current date
  useEffect(() => {
    const updateDate = () => {
      const now = new Date();
      // Format date in Spanish locale for Buenos Aires timezone
      const formattedDate = format(now, 'dd/MM/yyyy', { locale: es });
      setCurrentDate(formattedDate);
    };

    // Set initial date
    updateDate();

    // Update date every minute
    const interval = setInterval(updateDate, 60000);

    return () => clearInterval(interval);
  }, []);
  
  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700">
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <button
              type="button"
              className="text-gray-500 dark:text-gray-400 lg:hidden focus:outline-none"
              onClick={toggleMobileSidebar}
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="ml-3 lg:ml-0 text-lg font-semibold text-gray-900 dark:text-white">{title}</h1>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Current Date */}
            <div className="hidden md:flex items-center text-gray-500 dark:text-gray-400">
              <span className="text-sm capitalize">{currentDate}</span>
            </div>

           <button 
              className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
              onClick={toggleDarkMode}
              aria-label={darkMode ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
            >
              {darkMode ? <Sun className="h-6 w-6" /> : <Moon className="h-6 w-6" />}
            </button>
            
            {/* To-Do List Icon - Only for Admins */}
            {isAdmin && (
              <button 
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                onClick={() => navigate('/todolist')}
                aria-label="To-Do Diario"
              >
                <ListTodo className="h-6 w-6" />
              </button>
            )}

            {(isAdmin || userRole === 'Vendedor') && (
              <button 
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                onClick={() => navigate('/cotizador')}
                aria-label="Cotizador"
              >
                <Calculator className="h-6 w-6" />
              </button>
            )}

            {isAdmin && (
              <button 
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                onClick={() => navigate('/productionmanager')}
                aria-label="Producción"
                title="Producción"
              >
                <Wrench className="h-6 w-6" />
              </button>
            )}
            
            {isAdmin && (
              <button 
                className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none"
                onClick={() => navigate('/configuracion')}
              >
                <Settings className="h-6 w-6" />
              </button>
            )}
            
            <button 
              className="p-1 text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 focus:outline-none"
              onClick={signOut}
            >
              <LogOut className="h-6 w-6" />
            </button>
            
            <div className="relative">
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-indigo-600 dark:bg-indigo-500 flex items-center justify-center text-white">
                  {user ? getUserInitials() : <User className="h-5 w-5" />}
                </div>
                <div className="ml-2 hidden sm:block">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {user?.email ? user.email.split('@')[0] : 'Usuario'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {getRoleDisplay()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;